import { Button, Form, Modal, notification } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import PromotionForm from "./promotion-form";
import { closeModal } from "store/slices/modalsSlice";
import {
  getPromotionById,
  updatePromotionById,
} from "store/slices/promotionsSlice";
import { IMAGE_URL } from "constants/ApiConstant";
import { buildPromotionPayload } from "./promotion-payload";

const EMPTY_LOCALIZED = { fr: "", en: "", ar: "" };

const toLocalized = (value) => ({
  ...EMPTY_LOCALIZED,
  ...(value || {}),
});

const toImageUrl = (imageValue) => {
  if (!imageValue) return null;
  if (typeof imageValue === "string") {
    if (/^https?:\/\//i.test(imageValue)) return imageValue;
    return `${IMAGE_URL}/${imageValue.replace(/^\/+/, "")}`;
  }

  const imagePath =
    imageValue?.path || imageValue?.url || imageValue?.src || imageValue?.filename;

  if (!imagePath) return null;
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  return `${IMAGE_URL}/${String(imagePath).replace(/^\/+/, "")}`;
};

const toFormationIds = (formationIds, formations) => {
  const source = formationIds || formations || [];
  return source
    .map((item) => item?._id || item?.id || item)
    .filter(Boolean);
};

const EditPromotionModal = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const { isModalOpen, modalType, modalProps } = useSelector((s) => s.modals);
  const { promotion, loading } = useSelector((s) => s.promotions);

  const open = isModalOpen && modalType === "edit_promotion";
  const mainImageSource = useMemo(() => {
    if (!promotion) return null;
    return promotion?.mainImage || promotion?.heroImage || null;
  }, [promotion]);
  const hasExistingMainImage = Boolean(mainImageSource);

  useEffect(() => {
    if (open && modalProps?.id) {
      dispatch(getPromotionById(modalProps.id));
    }
  }, [open, modalProps?.id, dispatch]);

  useEffect(() => {
    if (!promotion) return;
    const imageUrl = toImageUrl(mainImageSource);
    const imageFileList = imageUrl
      ? [
          {
            uid: "-1",
            name: "main-image.png",
            status: "done",
            url: imageUrl,
          },
        ]
      : [];

    form.setFieldsValue({
      badge: toLocalized(promotion.badge),
      mainTitle: toLocalized(promotion.mainTitle || promotion.title),
      mainDescription: toLocalized(
        promotion.mainDescription || promotion.description
      ),
      offerTitle: toLocalized(promotion.offerTitle),
      offerDescription: toLocalized(promotion.offerDescription),
      formations: toFormationIds(promotion.formations, promotion.formationIds),
      journeyTitle: toLocalized(promotion.journeyTitle || promotion.journyTitle),
      journeyDescription: toLocalized(
        promotion.journeyDescription || promotion.journydiscription
      ),
      journeySteps: (promotion.journeySteps || promotion.steps || []).map((step) => ({
        title: toLocalized(step?.title),
        description: toLocalized(step?.description),
      })),
      startDate: dayjs(promotion.startDate),
      endDate: dayjs(promotion.endDate),
      mainImage: imageFileList,
    });
  }, [promotion, form, mainImageSource]);

  const onSubmit = async (values) => {
    try {
      const payload = buildPromotionPayload({
        values,
        isEditing: true,
        hadMainImage: hasExistingMainImage,
      });

      await dispatch(updatePromotionById({
        id: modalProps.id,
        data: payload,
      })).unwrap();

      notification.success({ message: "Promotion updated" });
      onClose();
    } catch (error) {
      const description =
        typeof error === "string" ? error : "Update failed";
      notification.error({ message: description });
    }
  };

  const onClose = () => {
    form.resetFields();
    dispatch(closeModal());
  };

  return (
    <Modal
      title="Edit Promotion"
      open={open}
      onCancel={onClose}
      width={980}
      destroyOnClose
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          form="edit_promotion_form"
          loading={loading}
        >
          Save
        </Button>,
      ]}
    >
      <PromotionForm
        form={form}
        onSubmit={onSubmit}
        isEditing
        formId="edit_promotion_form"
      />
    </Modal>
  );
};

export default EditPromotionModal;
